import { privateApi as api } from '../../../../api/backend/config';
import { IUserRepo } from '../../interfaces/users.interface';
import {
  PutUserAddressRequest,
  PutUserAddressResponse,
} from '../../../models/users/address/put-user-address.model';
import {
  PostUserProfileRequest,
  PostUserProfileResponse,
} from '../../../models/users/profile/post-user-profile.model';
import {
  PostUserSocialRequest,
  PostUserSocialResponse,
} from '../../../models/users/socials/post-user-social.model';
import {
  GetWalletVerificationMessageRequest,
  GetWalletVerificationMessageResponse,
} from '../../../models/users/wallet/get-wallet-verification-message.model';
import {
  PostUserWalletRequest,
  PostUserWalletResponse,
} from '../../../models/users/wallet/post-user-wallet.model';
import {
  PostUserSubmitApplicationRequest,
  PostUserSubmitApplicationResponse,
} from 'core/models/users/submit/post-user-submit-application.model';
import {
  GetUserReviewApplicationRequest,
  GetUserReviewApplicationResponse,
} from 'core/models/users/review/get-user-review-application.model';
import {
  GetUserNFTByIdRequest,
  GetUserNFTByIdResponse,
} from 'core/models/users/wallet/get-nft-by-id.model';
import {
  PostCreateOrLoadUserRequest,
  PostCreateOrLoadUserResponse,
} from 'core/models/users/post-create-load-user.model';
import {
  GetUserKYCRequest,
  GetUserKYCResponse,
} from 'core/models/users/kyc/get-user-kyc.model';
import {
  GetUserMainWalletRequest,
  GetUserMainWalletResponse,
} from 'core/models/users/wallet/get-user-main-wallet.model';
import { WalletType } from 'core/models/users/wallet/wallet-address.model';
import {
  PostMintOrFetchNFTRequest,
  PostMintOrFetchNFTResponse,
} from 'core/models/users/wallet/post-mint-or-fetch-nft.model';
import {
  PostUserSmartWalletRequest,
  PostUserSmartWalletResponse,
} from 'core/models/users/wallet/post-smart-wallet.model';
import {
  GetUserWalletsRequest,
  GetUserWalletsResponse,
} from 'core/models/users/wallet/get-user-wallets.model';
import {
  GetUserProjectByIdRequest,
  GetUserProjectByIdResponse,
} from 'core/models/users/project/get-project-by-id.model';
import {
  GetClaimSignerRequest,
  GetClaimSignerResponse,
  GetMyProjectsRequest,
  GetMyProjectsResponse,
} from 'core/models/users/project/get-my-projects.model';
import {
  GetUserProjectsRequest,
  GetUserProjectsResponse,
} from 'core/models/users/project/get-user-projects.model';
import useGlobalStore from 'stores/globalStore';
import { CryptoNetworkConfig } from 'core/models/app-config/network/app-config-network.model';
import {
  GetProjectCardByIdRequest,
  GetProjectCardByIdResponse,
} from 'core/models/users/project/get-project-card-by-id.model';
import {
  GetRoundInvestmentsRequest,
  GetRoundInvestmentsResponse,
} from 'core/models/users/project/get-round-investments.model';
import {
  GetRoundInvestmentClaimsRequest,
  GetRoundInvestmentClaimsResponse,
} from 'core/models/users/project/get-round-investment-claims.model';
import {
  GetDiscordPulseResponse,
  GetDiscordPulseByIdRequest,
} from 'core/models/app-config/get-discord-pulse.model';
import {
  GetFeedsRequest,
  GetFeedsResponse,
} from 'core/models/users/project/get-feeds.model';

export class UserV1BaseServerRepo implements IUserRepo {
  private readonly repoUrl =
    process.env.REACT_APP_REPOSITORY_BASESERVER + '/api/v1/users';
  cryptoNetworks: CryptoNetworkConfig[] =
    useGlobalStore.getState().cryptoNetworks;

  public async createOrLoad(
    request: PostCreateOrLoadUserRequest
  ): Promise<PostCreateOrLoadUserResponse> {
    const url = this.repoUrl;
    const response = await api.post(url, request);
    return response.data;
  }

  public async updateAddress(
    request: PutUserAddressRequest
  ): Promise<PutUserAddressResponse> {
    const url = this.repoUrl + '/address';

    const response = await api.put(url, request);

    return response.data;
  }

  public async createProfile(
    request: PostUserProfileRequest
  ): Promise<PostUserProfileResponse> {
    const url = this.repoUrl + '/profile';

    const response = await api.post(url, request);

    return response.data;
  }

  public async createSocials(
    request: PostUserSocialRequest
  ): Promise<PostUserSocialResponse> {
    const url = this.repoUrl + '/socials';
    const response = await api.post(url, request);
    return response.data;
  }

  public async createTwitter(request: {}): Promise<PostUserSocialResponse> {
    const url = this.repoUrl + '/twitter';
    const response = await api.post(url, request);
    return response.data;
  }

  public async updateSocials(
    request: PostUserSocialRequest
  ): Promise<PostUserSocialResponse> {
    const url = this.repoUrl + '/socials';
    const response = await api.put(url, request);
    return response.data;
  }

  public async walletVerificationMessage(
    request: GetWalletVerificationMessageRequest
  ): Promise<GetWalletVerificationMessageResponse> {
    const url = this.repoUrl + `/wallets/${request.address}/verification_msg`;
    const response = await api.get(url);
    return response.data;
  }

  public async addWallet(
    request: PostUserWalletRequest
  ): Promise<PostUserWalletResponse> {
    const url = this.repoUrl + '/wallets';
    const response = await api.post(url, request);
    return response.data;
  }

  public async reviewApplication(
    request: GetUserReviewApplicationRequest
  ): Promise<GetUserReviewApplicationResponse> {
    const url = this.repoUrl + '/review-application';
    const response = await api.get(url, { params: request });
    return response.data;
  }

  public async submitApplication(
    request: PostUserSubmitApplicationRequest
  ): Promise<PostUserSubmitApplicationResponse> {
    const url = this.repoUrl + '/submit-application';
    const response = await api.post(url, request);
    return response.data;
  }

  public async getKYC(request: GetUserKYCRequest): Promise<GetUserKYCResponse> {
    const url = this.repoUrl + '/kyc';
    const response = await api.get(url, { params: request });
    return response.data;
  }

  public async getWallets(
    request: GetUserWalletsRequest
  ): Promise<GetUserWalletsResponse> {
    const url = this.repoUrl + '/wallets';
    const response = await api.get(url, { params: request });
    return response.data.data;
  }

  public async getMainWallet(
    request: GetUserMainWalletRequest
  ): Promise<GetUserMainWalletResponse> {
    request = {
      filter: `{"walletType": "${WalletType.MAIN}"}`,
      ...request,
    };
    const url = this.repoUrl + '/wallets';
    const response = await api.get(url, { params: request as any });
    return response.data?.data?.[0];
  }

  public async mintORFetchNFT(
    request: PostMintOrFetchNFTRequest
  ): Promise<PostMintOrFetchNFTResponse> {
    const url = this.repoUrl + '/wallet/nft';
    const response = await api.post(url, request);
    return response.data;
  }

  public async nftUri(
    request: GetUserNFTByIdRequest
  ): Promise<GetUserNFTByIdResponse> {
    const url = this.repoUrl + `/wallet/nft/${request.id}`;
    const response = await api.get(url);
    return response.data;
  }

  public async smartWallet(
    request: PostUserSmartWalletRequest
  ): Promise<PostUserSmartWalletResponse> {
    const url = this.repoUrl + '/wallet/smartWallet';
    const response = await api.post(url, request);
    return response.data;
  }

  public async getProjectById(
    request: GetUserProjectByIdRequest
  ): Promise<GetUserProjectByIdResponse> {
    const url = this.repoUrl + `/projects/${request.id}`;
    const response = await api.get(url);

    return response.data;
  }

  public async getRoundInvestments(
    request: GetRoundInvestmentsRequest
  ): Promise<GetRoundInvestmentsResponse> {
    try {
      const url = this.repoUrl + `/rounds/${request.id}/investments`;
      const response = await api.get(url);

      return response.data.investments;
    } catch (error) {
      console.error({ error, endpoint: 'investments' });
      throw error;
    }
  }

  public async getRoundInvestmentClaims(
    request: GetRoundInvestmentClaimsRequest
  ): Promise<GetRoundInvestmentClaimsResponse> {
    try {
      const url = this.repoUrl + `/investments/${request.id}/claims`;
      const response = await api.get(url);

      return response.data;
    } catch (error) {
      console.error({ error, endpoint: 'investments' });
      throw error;
    }
  }

  public async getMyProjects(
    request: GetMyProjectsRequest
  ): Promise<GetMyProjectsResponse> {
    const url = this.repoUrl + `/projects/my`;
    const response = await api.get(url, { params: request });

    return response.data.data;
  }

  public async getClaimSigner(
    request: GetClaimSignerRequest
  ): Promise<GetClaimSignerResponse> {
    const url = this.repoUrl + `/investment/${request.investmentId}/claim`;
    const response = await api.get(url);

    return response.data;
  }

  public async getProjects(
    request: GetUserProjectsRequest
  ): Promise<GetUserProjectsResponse> {
    const url = this.repoUrl + `/projects`;
    const response = await api.get(url, { params: request });

    return response.data.data;
  }

  public async getFeaturedProjects(): Promise<GetUserProjectsResponse> {
    const response = await this.getProjects({ pageSize: 100 });
    const featuredProject = response.filter(({ featured }) => featured);
    return featuredProject.length > 0 ? featuredProject : response.slice(0, 1);
  }

  public async getProjectCardById(
    request: GetProjectCardByIdRequest
  ): Promise<GetProjectCardByIdResponse> {
    const url =
      this.repoUrl + `/projects/${request.projectId}/cards/${request.id}`;
    const response = await api.get(url);
    return response.data;
  }
  public async getDiscordPulseById({
    id,
    ...params
  }: GetDiscordPulseByIdRequest): Promise<GetDiscordPulseResponse> {
    const url = this.repoUrl + `/projects/${id}/discordPulse`;
    const response = await api.get(url, { params });
    return response.data;
  }

  public async getFeeds(request: GetFeedsRequest): Promise<GetFeedsResponse> {
    const url = this.repoUrl + `/feeds`;
    const response = await api.get(url, { params: request });

    return response.data;
  }
}
